import { ATTACHMENT_DEFAULTS } from "@/app/lib/constants";

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
   png: "image/png",
   jpg: "image/jpeg",
   jpeg: "image/jpeg",
   gif: "image/gif",
   webp: "image/webp",
   heic: "image/heic",
   heif: "image/heif",
   pdf: "application/pdf",
   docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
   xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
   txt: "text/plain",
   md: "text/markdown",
   markdown: "text/markdown",
   csv: "text/csv",
   webm: "audio/webm",
   ogg: "audio/ogg",
   oga: "audio/ogg",
   m4a: "audio/mp4",
   mp3: "audio/mpeg",
   wav: "audio/wav"
};

export const normalizeAttachmentFile = (file: File): File => {
   if (file.type && file.type.toLowerCase() !== "application/octet-stream") return file;
   const mimetype =
      MIME_TYPE_BY_EXTENSION[file.name.split(".").pop()?.toLowerCase() || ""];
   return mimetype
      ? new File([file], file.name, { type: mimetype, lastModified: file.lastModified })
      : file;
};

export const isHeicImage = (file: File): boolean =>
   ["image/heic", "image/heif"].includes(file.type.toLowerCase()) ||
   /\.(heic|heif)$/i.test(file.name);

export const isSupportedImageSource = (file: File): boolean =>
   ["image/png", "image/jpeg", "image/gif", "image/webp"].includes(
      file.type.toLowerCase()
   ) || isHeicImage(file);

const canvasBlob = (
   canvas: HTMLCanvasElement,
   mimetype: string,
   quality: number
): Promise<Blob> =>
   new Promise((resolve, reject) => {
      canvas.toBlob(
         (blob) => (blob ? resolve(blob) : reject(new Error("imageOptimizationFailed"))),
         mimetype,
         quality
      );
   });

const outputName = (name: string, mimetype: string): string => {
   const extension =
      mimetype === "image/jpeg" ? ".jpg" : mimetype === "image/png" ? ".png" : ".webp";
   return `${name.replace(/\.[^.]+$/, "") || "image"}${extension}`;
};

export const prepareAttachmentImage = async (
   source: File,
   maxUploadSizeMB: number
): Promise<File> => {
   if (source.size > ATTACHMENT_DEFAULTS.MAX_IMAGE_SOURCE_MB * 1024 * 1024) {
      throw new Error("imageSourceTooLarge");
   }
   const maxUploadBytes = maxUploadSizeMB * 1024 * 1024;
   const sourceType = source.type.toLowerCase();
   if (sourceType === "image/gif") {
      if (source.size > maxUploadBytes) throw new Error("tooLarge");
      const header = new DataView(await source.slice(0, 10).arrayBuffer());
      const width = header.byteLength >= 10 ? header.getUint16(6, true) : 0;
      const height = header.byteLength >= 10 ? header.getUint16(8, true) : 0;
      if (
         width <= 0 ||
         height <= 0 ||
         width > ATTACHMENT_DEFAULTS.MAX_IMAGE_DIMENSION ||
         height > ATTACHMENT_DEFAULTS.MAX_IMAGE_DIMENSION
      ) {
         throw new Error("imageDimensionsTooLarge");
      }
      return source;
   }

   const heic = isHeicImage(source);
   let imageBlob: Blob = source;
   if (heic) {
      const { default: heic2any } = await import("heic2any");
      const converted = await heic2any({
         blob: source,
         toType: "image/jpeg",
         quality: 0.9
      });
      imageBlob = Array.isArray(converted) ? converted[0] : converted;
   }

   const bitmap = await createImageBitmap(imageBlob, { imageOrientation: "from-image" });
   const outputType = heic ? "image/jpeg" : sourceType;
   let scale = Math.min(
      1,
      ATTACHMENT_DEFAULTS.MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height)
   );
   let quality = 0.9;

   try {
      for (let attempt = 0; attempt < 12; attempt += 1) {
         const canvas = document.createElement("canvas");
         canvas.width = Math.max(1, Math.round(bitmap.width * scale));
         canvas.height = Math.max(1, Math.round(bitmap.height * scale));
         const context = canvas.getContext("2d");
         if (!context) throw new Error("imageOptimizationFailed");
         context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

         const blob = await canvasBlob(canvas, outputType, quality);
         if (blob.size <= maxUploadBytes) {
            return new File([blob], outputName(source.name, outputType), {
               type: outputType,
               lastModified: source.lastModified
            });
         }

         if (outputType !== "image/png" && quality > 0.66) {
            quality -= 0.08;
         } else {
            scale *= 0.8;
            quality = 0.9;
         }
      }
   } finally {
      bitmap.close();
   }

   throw new Error("imageOptimizationFailed");
};
